/**
 * MemberOrgSwitcher Component
 * Dropdown for members to switch between organizations they belong to.
 * Simpler version of the staff OrganizationSwitcher.
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Building, ChevronDown, Check, Loader2 } from 'lucide-react';
import {
  fetchMemberOrganizations,
  getMemberOrganizations,
  switchMemberOrganization,
  getMemberData,
  type MemberOrganization,
} from '@/lib/memberAuth';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface MemberOrgSwitcherProps {
  onOrgChange?: () => void;
}

export function MemberOrgSwitcher({ onOrgChange }: MemberOrgSwitcherProps) {
  const { t } = useAppTranslation();
  const [organizations, setOrganizations] = useState<MemberOrganization[]>(getMemberOrganizations());
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const member = getMemberData();
  const currentOrgId = member?.organization_id;

  useEffect(() => {
    fetchMemberOrganizations()
      .then(setOrganizations)
      .catch(() => {
        // Fall back to cached orgs
      });
  }, []);

  // Don't render if member only has 0-1 orgs
  if (organizations.length <= 1) return null;

  const currentOrg = organizations.find((o) => o.cbo_id === currentOrgId);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSwitch = async (orgId: number) => {
    if (orgId === currentOrgId || switching) return;
    try {
      setSwitching(true);
      await switchMemberOrganization(orgId);
      setIsOpen(false);
      onOrgChange?.();
      // Reload the page to refresh all data with new org context
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch organization:', err);
      setSwitching(false);
    }
  };

  // Outside-click closing is handled by the full-screen portal overlay's
  // onClick below. A document `mousedown` listener here was ALSO firing — but
  // the dropdown is rendered in a portal (document.body), so it isn't inside
  // buttonRef; a mousedown on an org item closed the menu before the item's
  // onClick could run, so switching never happened. (Removed.)

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm"
      >
        <Building className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
        <span className="text-gray-700 dark:text-gray-200 truncate max-w-[160px]">
          {currentOrg?.organization_name || 'Organization'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999]"
            onClick={() => setIsOpen(false)}
          >
            <div
              className="absolute bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-72 overflow-hidden"
              style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('member.switch_organization', 'Switch Organization')}
                </p>
              </div>

              <div className="max-h-64 overflow-y-auto py-1">
                {organizations.map((org) => {
                  const isActive = org.cbo_id === currentOrgId;
                  return (
                    <button
                      key={org.cbo_id}
                      onClick={() => handleSwitch(org.cbo_id)}
                      disabled={switching}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        isActive
                          ? 'bg-teal-50 dark:bg-teal-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      } ${switching ? 'opacity-50 cursor-wait' : ''}`}
                    >
                      <Building
                        className={`w-4 h-4 flex-shrink-0 ${
                          isActive ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400'
                        }`}
                      />
                      <span
                        className={`flex-1 text-sm truncate ${
                          isActive
                            ? 'font-medium text-teal-700 dark:text-teal-300'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {org.organization_name}
                      </span>
                      {isActive && <Check className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />}
                      {switching && !isActive && (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
